// ai/pipelines/logo-slot.ts
//
// A LOGO SLOT THAT IS NOT A LOGO ELEMENT.
//
// The library's saved slides, and people's own covers, often carry the brand
// mark as an ordinary Image: a small picture, a fifth of the column wide,
// sitting above the title. Every pipeline that fills pictures read it as a
// picture slot -- the funnel put a photograph in it, and Add with AI put the
// cover's mark in it AND in the hero beside it, because to the pipeline they
// were two picture slots on a cover.
//
// So the slot is recognised by what it is rather than by what it is called:
// an Image no wider than LOGO_SLOT_MAX_WIDTH percent, in the same group as a
// heading. The brand-logo passes fill it like a Logo element; the picture
// passes leave it alone.

import { spliceWithSpacing } from "../add-slide/tidy-spacers";
import { isKeptPicture } from "./kept-picture";

export const LOGO_SLOT_MAX_WIDTH = 30;

type Node = {
  type?: string;
  settings?: Record<string, unknown>;
  children?: unknown;
};

const asNodes = (children: unknown): Node[] =>
  Array.isArray(children) ? (children as Node[]) : [];

/** A RichText that reads as the slide's heading. */
const isHeading = (node: unknown): boolean => {
  const settings = (node as Node | null)?.settings;
  if (!settings) return false;
  if (settings.name !== "RichText" && settings.name !== "CollapsibleText") {
    return false;
  }
  if (/title/i.test(String(settings.textStyle ?? ""))) return true;
  return /<h[12][\s>]/i.test(String(settings.content ?? ""));
};

/**
 * Whether this Image is the mark beside a heading rather than a picture.
 *
 * `siblings` are the other children of the group it sits in; the rule needs
 * them because a small picture on its own is just a small picture.
 */
export const isLogoSlotImage = (
  settings: Record<string, unknown> | null | undefined,
  siblings: unknown,
): boolean => {
  if (!settings || settings.name !== "Image") return false;
  const width = Number(settings.width);
  if (!Number.isFinite(width) || width <= 0 || width > LOGO_SLOT_MAX_WIDTH) {
    return false;
  }
  return asNodes(siblings).some(isHeading);
};

/** Whether the slide has such a slot anywhere. */
export const hasLogoSlot = (slide: unknown): boolean => {
  const visit = (node: Node, siblings: Node[]): boolean => {
    if (!node || typeof node !== "object") return false;
    if (node.type === "element" && isLogoSlotImage(node.settings, siblings)) {
      return true;
    }
    const children = asNodes(node.children);
    return children.some((child) => visit(child, children));
  };
  return visit(slide as Node, []);
};

/**
 * `slide` with every logo slot's settings replaced by `update(settings)`.
 * Returns new objects along the changed paths and the same slide when there
 * is no slot.
 */
export const mapLogoSlots = <T>(
  slide: T,
  update: (settings: Record<string, unknown>) => Record<string, unknown>,
): T => {
  let changed = false;
  const visit = (node: Node, siblings: Node[]): Node => {
    if (!node || typeof node !== "object") return node;
    if (
      node.type === "element" &&
      isLogoSlotImage(node.settings, siblings) &&
      // A mark the team uploaded is theirs to keep (./kept-picture).
      !isKeptPicture(node.settings)
    ) {
      changed = true;
      return { ...node, settings: update(node.settings ?? {}) };
    }
    if (!Array.isArray(node.children)) return node;
    const children = asNodes(node.children);
    const next = children.map((child) => visit(child, children));
    return next.some((child, i) => child !== children[i])
      ? { ...node, children: next }
      : node;
  };
  const out = visit(slide as unknown as Node, []);
  return (changed ? out : slide) as T;
};

/**
 * The mark written into every logo slot: the url, and the free shape, so a
 * wordmark is contained rather than cropped to the frame.
 */
export const setLogoSlots = <T>(slide: T, url: string): T =>
  mapLogoSlots(slide, (settings) => ({ ...settings, src: url, shape: "free" }));

// A library slot's own picture: every Image src in the layout export lives in
// the Storydoc asset bucket, and so does the editor's placeholder graphic.
const LIBRARY_PLACEHOLDER = /^https:\/\/assets(-staging)?\.storydoc\.com\//;

/**
 * `slide` without the logo slots nothing filled: a mark the layout ships for
 * a brand the deck does not have. Left in, the slot showed the layout's own
 * placeholder, or was handed to a stock search and came back as a photograph
 * of the word "and" above a closing title (Dawid, 2026-10-11). It goes with
 * its spacing, so the title moves up into the space.
 *
 * Returns the SAME object when there is no such slot.
 */
export const dropUnfilledLogoSlots = <T>(slide: T): T => {
  if (!slide || typeof slide !== "object") return slide;
  const next = structuredClone(slide) as unknown as Node;
  let removed = 0;
  const visit = (node: Node) => {
    if (!Array.isArray(node?.children)) return;
    const children = node.children as Node[];
    const empty = children.filter(
      (child) =>
        child?.type === "element" &&
        isLogoSlotImage(child.settings, children) &&
        LIBRARY_PLACEHOLDER.test(String(child.settings?.src ?? "")) &&
        !isKeptPicture(child.settings),
    );
    removed += spliceWithSpacing(children, empty);
    for (const child of children) visit(child);
  };
  visit(next);
  return removed > 0 ? (next as unknown as T) : slide;
};
